import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";

const PROMPTS = [
  "When you are alone with yourself — no one watching — who do you become?",
  "Listen to the words you use about yourself. What do they quietly confess?",
  "Every word you speak is a vibration shaping your world. What reality have your words been creating?",
  "In your relationships, what pattern keeps returning to you like a reflection?",
  "What reaction in you belongs to an old wound, not to this moment?",
  "What are you still unwilling to forgive — in another, or in yourself?",
  "What truth have you been avoiding saying out loud?",
  "If there were truly no judgment here — what would you finally admit?",
  "This light was always within you. What would change if you lived from it?",
];

export default function TheMirror() {
  const { user } = useAuth();
  const isPaid = user && (user.tier === "seeker" || user.tier === "contributor");
  const [started, setStarted] = useState(false);
  const [depth, setDepth] = useState(0);
  const [done, setDone] = useState(false);
  const [reflection, setReflection] = useState("");
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const total = PROMPTS.length;
  // glow grows as they go deeper (and blooms fully at completion)
  const progress = done ? 1 : started ? (depth + 1) / total : 0.06;
  const question = aiQuestion || PROMPTS[depth];

  const deeper = () => {
    setReflection("");
    setAiQuestion("");
    if (depth + 1 >= total) setDone(true);
    else setDepth((d) => d + 1);
  };

  const askMirror = async () => {
    if (!isPaid) return;
    setAiLoading(true);
    try {
      const { data } = await api.get("/mirror/reflect");
      setAiQuestion(data.result.question);
      setReflection("");
    } catch {
      toast.error("The mirror is quiet just now. Try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const restart = () => { setStarted(false); setDepth(0); setDone(false); setReflection(""); setAiQuestion(""); };

  return (
    <main data-testid="the-mirror-page" className="relative min-h-[calc(100vh-64px)] grid place-items-center px-6 overflow-hidden">
      {/* The reflective surface */}
      <div
        className="absolute inset-0 -z-10"
        style={{ background: "radial-gradient(1000px 700px at 50% 42%, rgba(212,175,55,0.10), transparent 60%), #0d0a08" }}
      />
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 rounded-full transition-all duration-[2500ms] ease-out slow-pulse"
        data-testid="mirror-glow"
        style={{
          height: "60vmin", width: "60vmin",
          background: "radial-gradient(circle, rgba(245,224,150,0.9), rgba(212,175,55,0.35) 42%, transparent 68%)",
          opacity: 0.08 + progress * 0.72,
          filter: `blur(${28 - progress * 12}px)`,
          transform: `translate(-50%,-50%) scale(${0.55 + progress * 0.7})`,
        }}
      />

      <div className="relative w-full max-w-2xl">
        <div className="relative glass rounded-[2rem] overflow-hidden border border-[#D4AF37]/20"
          style={{ boxShadow: `0 0 ${40 + progress * 120}px rgba(212,175,55,${0.1 + progress * 0.25})` }}>
          {/* moving sheen on the glass */}
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="mirror-sheen absolute top-0 h-full w-1/3"
              style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)" }} />
          </div>

          <div className="relative p-10 sm:p-14 text-center min-h-[420px] flex flex-col items-center justify-center">
            {!started && !done && (
              <div className="fade-up" data-testid="mirror-intro">
                <p className="text-[#D4AF37] tracking-[0.35em] uppercase text-xs mb-6">The Mirror</p>
                <h1 className="serif text-4xl sm:text-5xl font-light text-[#F7F1E6] leading-tight">
                  Are you ready <span className="italic-serif text-[#D4AF37]">to look?</span>
                </h1>
                <p className="mt-5 text-[#B4A897] leading-relaxed max-w-md mx-auto">
                  There is no judgment here — only what is true. All creation lives on the vibration of
                  the words and energy you carry; with them, you are shaping your reality. The mirror shows
                  only what you are willing to see, and brightens as you let your own light glow.
                </p>
                <button data-testid="mirror-begin" onClick={() => setStarted(true)}
                  className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-8 py-3.5 font-medium hover:bg-[#e6c860] transition-colors duration-300 gold-glow">
                  Look into the mirror
                </button>
              </div>
            )}

            {started && !done && (
              <div className="w-full fade-up" key={depth + (aiQuestion ? "-ai" : "")}>
                <p className="text-[#D4AF37]/70 tracking-[0.25em] uppercase text-[10px] mb-5">
                  {aiQuestion ? "The mirror reflects you" : `Deeper · ${depth + 1} of ${total}`}
                </p>
                <p data-testid="mirror-question" className="serif text-2xl sm:text-3xl text-[#F7F1E6] leading-snug">
                  {question}
                </p>
                <textarea
                  data-testid="mirror-reflection"
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  rows={3}
                  placeholder="Speak it to the glass… (only you will ever see this)"
                  className="mt-7 w-full bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-4 text-center text-[#F7F1E6] italic-serif text-lg outline-none focus:border-[#D4AF37]/50 transition-colors duration-300 resize-none placeholder:text-[#8a7f70]"
                />
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <button data-testid="mirror-deeper" onClick={deeper}
                    className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-7 py-3 font-medium hover:bg-[#e6c860] transition-colors duration-300">
                    {depth + 1 >= total ? "Rest in the light" : "Go deeper"} <ArrowRight size={17} />
                  </button>
                  {isPaid ? (
                    <button data-testid="mirror-ask" onClick={askMirror} disabled={aiLoading}
                      className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-6 py-3 text-sm hover:bg-[#D4AF37]/10 transition-colors duration-300 disabled:opacity-50">
                      {aiLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Ask the mirror to reflect me
                    </button>
                  ) : (
                    <Link data-testid="mirror-ask-upgrade" to="/pricing"
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 text-[#B4A897] px-6 py-3 text-sm hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-colors duration-300">
                      <Sparkles size={15} /> Let the mirror reflect you (Seeker)
                    </Link>
                  )}
                </div>
              </div>
            )}

            {done && (
              <div className="fade-up" data-testid="mirror-complete">
                <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-6">The light within</p>
                <h2 className="serif text-3xl sm:text-4xl text-[#F7F1E6] leading-snug">
                  This light was <span className="italic-serif text-[#D4AF37]">always yours.</span>
                </h2>
                <p className="mt-5 text-[#B4A897] leading-relaxed max-w-md mx-auto">
                  You looked, without flinching and without judgment. Carry this clarity gently into
                  your day, and let it touch everyone you meet.
                </p>
                <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                  <Link to="/journal" data-testid="mirror-to-journal"
                    className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#17100C] px-7 py-3 font-medium hover:bg-[#e6c860] transition-colors duration-300">
                    Write what you saw <ArrowRight size={17} />
                  </Link>
                  <button data-testid="mirror-restart" onClick={restart}
                    className="rounded-full border border-white/15 text-[#B4A897] px-7 py-3 text-sm hover:text-[#F7F1E6] transition-colors duration-300">
                    Look again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
